/*!
  Pioneer WFRIA Drawer v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Drawer"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Drawer") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Drawer");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["noAnimation", "children", "className", "onClose", "open", "title", "trigger", "width", "headerDock", "headerDockAriaLabel", "customStrings", "aria-label", "elementToFocusOnClose"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import { requiredIf } from "../common/proptypes";
import Panel from "../Panel/Panel";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
export const ANIMATION_TIMEOUT = 300;
/**
 * The Drawer component (also known as the Right Rail) is a panel that slides out from the edge of the screen.
 * It can be useful when you need users to organize notifications or
 * view some details without leaving the current page.
 * Build is based on the panel component.
 */

function Drawer(_ref) {
  let {
    noAnimation,
    children,
    className,
    onClose,
    open,
    title,
    trigger,
    width,
    headerDock,
    headerDockAriaLabel,
    customStrings,
    "aria-label": ariaLabel,
    elementToFocusOnClose
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    drawerStyles
  } = extractTheme(_objectSpread({
    theme,
    width
  }, otherProps));
  return (
    /**
     * We're restricting few props footer and height as these are not required for Drawer
     * and it might lead to UI issues
     */
    _jsx(Panel, _objectSpread(_objectSpread({
      animated: !noAnimation,
      animationClass: "pnr-drawer-animation",
      animationTimeout: ANIMATION_TIMEOUT,
      "aria-label": ariaLabel,
      className: className,
      customStrings: customStrings,
      customStyles: drawerStyles,
      elementToFocusOnClose: elementToFocusOnClose,
      headerDock: headerDock,
      headerDockAriaLabel: headerDockAriaLabel,
      onClose: onClose,
      open: open,
      title: title,
      trigger: trigger,
      width: width,
      closeOnDocumentClick: true
    }, otherProps), {}, {
      footer: null,
      height: null,
      children: children
    }))
  );
}

Drawer.propTypes = process.env.NODE_ENV !== "production" ? {
  /** This is what will be displayed inside the Panel. */
  children: PropTypes.node.isRequired,

  /**
   * aria-label for the Panel when `title` prop
   * is not configured.
   */
  "aria-label": requiredIf(PropTypes.string, (props, propName) => !props.title && !props[propName], "if `title` is not configured"),
  className: PropTypes.string,

  /**
   * Override for strings used within the component.
   *  - `closeButtonAriaLabel`: aria-label for the Panel's
   *     close button (this is what is displayed in the
   *     Button's tooltip).
   *     Default to "close <title> dialog".
   */
  customStrings: PropTypes.shape({
    closeButtonAriaLabel: PropTypes.string
  }),

  /**
   * This prop identifies the element that should receive
   * focus after Panel is closed.
   * It can be a css selector or a function which
   * returns a DOM node.
   *
   * This prop should only be used in rare cases (when for example,
   * the triggering element that opened the Panel is going to be
   * gone once the Panel closes.) In the majority of cases, the
   * Panel will automatically move the focus back to the element
   * that was used to trigger it.
   */
  elementToFocusOnClose: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * Sticky container located between the title and the
   * body of the Panel.
   */
  headerDock: PropTypes.node,

  /** aria-label for header dock container. */
  headerDockAriaLabel: PropTypes.string,

  /** Disables animation to the opening and closing states of the Panel. */
  noAnimation: PropTypes.bool,

  /**
   * Function that will be executed after the close button
   * is pressed.
   */
  onClose: PropTypes.func,

  /** Shows or hides the Panel. */
  open: PropTypes.bool,

  /** The title of the Panel. */
  title: PropTypes.string,
  trigger: PropTypes.node,

  /**
   * The width of the Panel at desktop breakpoint.
   * Must include the unit type. i.e. 200px or 40%.
   *
   * **Note**: Regardless of the value entered,
   * the calculated width will always be lower than 80% of
   * viewport width, and greater than 430px.
   */
  width: PropTypes.string
} : {};
Drawer.defaultProps = {
  "aria-label": null,
  className: null,
  headerDock: null,
  headerDockAriaLabel: null,
  noAnimation: false,
  onClose: null,
  open: false,
  width: null,
  title: null,
  trigger: null,
  elementToFocusOnClose: null,
  customStrings: null
};
export default Drawer;